import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { recommend, type CatalogItem, type QuizAnswers, type Vibe, type Stay, type Traveler, type Budget } from "@/lib/tripQuiz/recommend";
import {
  ArrowLeft, ArrowRight, MessageCircle, RefreshCw, ExternalLink,
  Users, Heart, Baby, PartyPopper, Waves, Compass, Activity, Utensils,
  Home, Hotel, Building2, Check, Sun, Calendar as CalendarIcon, Wallet,
} from "lucide-react";
import { cn } from "@/lib/utils";

const WHATSAPP_NUMBER = "306932377567";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type StepKey = "intro" | "traveler" | "days" | "vibe" | "stay" | "budget" | "results";
const FLOW: StepKey[] = ["intro", "traveler", "days", "vibe", "stay", "budget", "results"];

const TRAVELER_ICONS: Record<Traveler, React.ElementType> = {
  solo: Compass, couple: Heart, family: Baby, friends: Users,
};
const VIBE_ICONS: Record<Vibe, React.ElementType> = {
  relax: Sun, adventure: Waves, party: PartyPopper, culture: Utensils, family_fun: Activity,
};
const STAY_ICONS: Record<Stay, React.ElementType> = {
  boutique_hotels: Hotel, villas: Home, apartments: Building2, any: Check,
};

const defaultAnswers: QuizAnswers = {
  traveler: null, days: 5, vibes: [], stay: null, budget: null,
};

function hrefFor(item: CatalogItem): string {
  if (item.source === "accommodation") return `/accommodations/${item.slug}`;
  if (item.source === "boat") return `/private-boat-rentals-in-skiathos/${item.slug}`;
  // services
  switch (item.category) {
    case "cruise": return `/skiathos-daily-cruises/${item.slug}`;
    case "boat_rental": return `/private-boat-rentals-in-skiathos/${item.slug}`;
    case "boat_party": return `/skiathos-boat-party`;
    case "activity": return `/activities/${item.slug}`;
    case "transfer": return `/services/${item.slug}`;
    default: return `/services/${item.slug}`;
  }
}

const TripQuizDialog = ({ open, onOpenChange }: Props) => {
  const { t, i18n } = useTranslation("quiz");
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>(defaultAnswers);
  const [catalog, setCatalog] = useState<CatalogItem[] | null>(null);
  const [loading, setLoading] = useState(false);

  const step = FLOW[stepIdx];
  const stepNumber = stepIdx; // 0=intro
  const totalSteps = FLOW.length - 2; // exclude intro & results

  // Reset on close
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStepIdx(0);
        setAnswers(defaultAnswers);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Fetch catalog when reaching results
  useEffect(() => {
    if (step !== "results" || catalog) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [servicesRes, boatsRes] = await Promise.all([
          supabase.from("services").select("id,title,slug,category,image_url,price_label,capacity,tags,additional_categories").eq("approval_status", "approved"),
          supabase.from("boats").select("id,name,slug,image_url,price_per_day,capacity,category").eq("is_available", true),
        ]);
        const items: CatalogItem[] = [];
        
        const ACCOM_TYPE_KEY: Record<string, string> = {
          villas: "villas",
          boutique_hotel: "boutique_hotel",
          apartments: "apartments",
        };

        for (const s of servicesRes.data ?? []) {
          const isAcc = s.category === "accommodation" || (s.additional_categories || []).includes("accommodation");
          const priceLabel = (s as any).price_label as string | null;
          const numMatch = priceLabel?.match(/(\d+)/);
          const priceVal = numMatch ? parseFloat(numMatch[1]) : 0;

          if (isAcc) {
            const additionalCats: string[] = s.additional_categories || [];
            const accomType = additionalCats.find((c: string) => ACCOM_TYPE_KEY[c]) || "apartments";
            items.push({
              id: s.id,
              source: "accommodation",
              title: s.title,
              image: (s as any).image_url ?? null,
              slug: (s as any).slug,
              category: "accommodation",
              price: priceVal,
              priceLabel: priceLabel || (priceVal ? `€${priceVal}/night` : null),
              capacity: (s as any).capacity ?? null,
              tags: [accomType, ...(s as any).tags ?? []],
              href: "",
            });
          } else {
            items.push({
              id: s.id,
              source: "service",
              title: s.title,
              image: (s as any).image_url ?? null,
              slug: (s as any).slug,
              category: s.category as string,
              price: priceVal,
              priceLabel,
              capacity: (s as any).capacity ?? null,
              tags: (s as any).tags ?? [],
              href: "",
            });
          }
        }
        for (const b of boatsRes.data ?? []) {
          items.push({
            id: b.id, source: "boat", title: (b as any).name, image: (b as any).image_url,
            slug: (b as any).slug, category: "boat",
            price: Number((b as any).price_per_day) || 0,
            priceLabel: (b as any).price_per_day ? `€${(b as any).price_per_day}/day` : null,
            capacity: (b as any).capacity ?? null,
            tags: [], href: "",
          });
        }
        
        // Safety fallback: deduplicate displayed items by slug or id
        const seenIds = new Set<string>();
        const seenSlugs = new Set<string>();
        const deduplicatedItems: CatalogItem[] = [];
        
        for (const it of items) {
          let isDuplicate = false;
          if (it.id && seenIds.has(it.id)) {
            isDuplicate = true;
          }
          if (it.slug && seenSlugs.has(it.slug)) {
            isDuplicate = true;
          }
          if (!isDuplicate) {
            if (it.id) seenIds.add(it.id);
            if (it.slug) seenSlugs.add(it.slug);
            deduplicatedItems.push(it);
          }
        }

        deduplicatedItems.forEach((it) => (it.href = hrefFor(it)));
        if (!cancelled) setCatalog(deduplicatedItems);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, catalog]);

  const results = useMemo(() => {
    if (!catalog) return [];
    return recommend(catalog, answers, 6);
  }, [catalog, answers]);

  const goNext = () => setStepIdx((i) => Math.min(i + 1, FLOW.length - 1));
  const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));
  const restart = () => { setAnswers(defaultAnswers); setStepIdx(1); };

  const toggleVibe = (v: Vibe) => {
    setAnswers((a) => ({
      ...a,
      vibes: a.vibes.includes(v) ? a.vibes.filter((x) => x !== v) : [...a.vibes, v],
    }));
  };

  const canProceed = (): boolean => {
    switch (step) {
      case "traveler": return !!answers.traveler;
      case "days": return !!answers.days;
      case "vibe": return answers.vibes.length > 0;
      case "stay": return !!answers.stay;
      case "budget": return !!answers.budget;
      default: return true;
    }
  };

  const summaryKey = answers.traveler ?? "default";
  const tripPhrase = t(`results.summary.${summaryKey}`, t("results.summary.default"));

  const buildWhatsappUrl = () => {
    const list = results.map((r) => {
      const url = `${window.location.origin}${r.href}`;
      return `• ${r.title} — ${url}`;
    }).join("\n");
    const msg = t("results.whatsappMessage", { list });
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl w-screen sm:w-[95vw] h-[100dvh] sm:h-auto sm:max-h-[90vh] max-h-[100dvh] p-0 overflow-hidden border-0 sm:rounded-2xl rounded-none bg-gradient-to-br from-ocean-deep via-ocean-deep to-[#0a1f3a] text-primary-foreground translate-x-0 translate-y-0 left-0 top-0 sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]"
        aria-describedby={undefined}
      >
        {/* Progress */}
        {step !== "intro" && step !== "results" && (
          <div className="px-4 sm:px-6 pt-5 sm:pt-6 pb-2 pr-12 sm:pr-14">
            <div className="flex items-center justify-between mb-2 text-xs uppercase tracking-[0.15em] text-white/60">
              <span>{stepNumber} {t("of")} {totalSteps}</span>
              <button onClick={goBack} className="hover:text-white flex items-center gap-1 transition-colors">
                <ArrowLeft className="h-3 w-3" /> {t("back")}
              </button>
            </div>
            <Progress value={(stepNumber / totalSteps) * 100} className="h-1 bg-white/10" />
          </div>
        )}

        <div className="px-4 sm:px-6 md:px-10 pb-6 sm:pb-8 md:pb-10 pt-4 flex-1 min-h-0 h-full sm:h-auto sm:min-h-[480px] sm:max-h-[85vh] overflow-y-auto">
          {step === "intro" && (
            <div className="text-center py-10 animate-fade-in">
              <Activity className="h-12 w-12 mx-auto text-secondary mb-4" />
              <h2 className="font-display text-3xl md:text-5xl font-semibold mb-4 leading-tight">
                {t("intro.title")}
              </h2>
              <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto mb-8">
                {t("intro.subtitle")}
              </p>
              <Button
                onClick={() => setStepIdx(1)}
                className="h-14 px-10 rounded-full font-body font-semibold text-[13px] tracking-[0.14em] uppercase bg-secondary text-secondary-foreground hover:bg-secondary/90 hover:scale-[1.02] transition-all"
              >
                {t("intro.start")} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <p className="text-xs text-white/50 mt-4">{t("intro.duration")}</p>
            </div>
          )}

          {step === "traveler" && (
            <StepWrap title={t("steps.traveler.title")} subtitle={t("steps.traveler.subtitle")}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {(Object.keys(TRAVELER_ICONS) as Traveler[]).map((key) => {
                  const Icon = TRAVELER_ICONS[key];
                  const active = answers.traveler === key;
                  return (
                    <button
                      key={key}
                      onClick={() => { setAnswers((a) => ({ ...a, traveler: key })); setTimeout(goNext, 250); }}
                      className={cn(
                        "group flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all duration-200",
                        "bg-white/5 hover:bg-white/10 hover:scale-[1.03] backdrop-blur-sm",
                        active ? "border-secondary bg-white/10 scale-[1.03]" : "border-white/10"
                      )}
                    >
                      <Icon className={cn("h-8 w-8 transition-colors", active ? "text-secondary" : "text-white/80 group-hover:text-white")} />
                      <span className="text-sm font-medium text-center">{t(`steps.traveler.options.${key}`)}</span>
                    </button>
                  );
                })}
              </div>
            </StepWrap>
          )}

          {step === "days" && (
            <StepWrap title={t("steps.days.title")} subtitle={t("steps.days.subtitle")}>
              <div className="flex flex-col items-center gap-6 py-6">
                <div className="text-6xl md:text-7xl font-display font-semibold text-secondary">
                  {answers.days === 14 ? `${answers.days}+` : answers.days}
                </div>
                <div className="text-sm uppercase tracking-[0.2em] text-white/60">
                  {answers.days === 14
                    ? t("steps.days.valuePlus", { count: 14 })
                    : t("steps.days.value", { count: answers.days ?? 0 })}
                </div>
                <input
                  type="range"
                  min={1} max={14} step={1}
                  value={answers.days ?? 5}
                  onChange={(e) => setAnswers((a) => ({ ...a, days: parseInt(e.target.value, 10) }))}
                  className="w-full max-w-md accent-secondary"
                />
                <div className="flex justify-between w-full max-w-md text-[10px] uppercase tracking-wider text-white/40">
                  <span>1</span><span>7</span><span>14+</span>
                </div>
              </div>
            </StepWrap>
          )}

          {step === "vibe" && (
            <StepWrap title={t("steps.vibe.title")} subtitle={t("steps.vibe.subtitle")}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {(Object.keys(VIBE_ICONS) as Vibe[]).map((key) => {
                  const Icon = VIBE_ICONS[key];
                  const active = answers.vibes.includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => toggleVibe(key)}
                      className={cn(
                        "group relative flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all duration-200",
                        "bg-white/5 hover:bg-white/10 hover:scale-[1.03] backdrop-blur-sm",
                        active ? "border-secondary bg-white/10" : "border-white/10"
                      )}
                    >
                      {active && (
                        <span className="absolute top-2 right-2 h-5 w-5 rounded-full bg-secondary flex items-center justify-center">
                          <Check className="h-3 w-3 text-secondary-foreground" />
                        </span>
                      )}
                      <Icon className={cn("h-8 w-8", active ? "text-secondary" : "text-white/80 group-hover:text-white")} />
                      <span className="text-sm font-medium text-center">{t(`steps.vibe.options.${key}`)}</span>
                    </button>
                  );
                })}
              </div>
            </StepWrap>
          )}

          {step === "stay" && (
            <StepWrap title={t("steps.stay.title")} subtitle={t("steps.stay.subtitle")}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {(Object.keys(STAY_ICONS) as Stay[]).map((key) => {
                  const Icon = STAY_ICONS[key];
                  const active = answers.stay === key;
                  return (
                    <button
                      key={key}
                      onClick={() => { setAnswers((a) => ({ ...a, stay: key })); setTimeout(goNext, 250); }}
                      className={cn(
                        "group flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all duration-200",
                        "bg-white/5 hover:bg-white/10 hover:scale-[1.03] backdrop-blur-sm",
                        active ? "border-secondary bg-white/10" : "border-white/10"
                      )}
                    >
                      <Icon className={cn("h-8 w-8", active ? "text-secondary" : "text-white/80 group-hover:text-white")} />
                      <span className="text-sm font-medium text-center">{t(`steps.stay.options.${key}`)}</span>
                    </button>
                  );
                })}
              </div>
            </StepWrap>
          )}

          {step === "budget" && (
            <StepWrap title={t("steps.budget.title")} subtitle={t("steps.budget.subtitle")}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                {(["low", "mid", "premium"] as Budget[]).map((key) => {
                  const active = answers.budget === key;
                  return (
                    <button
                      key={key}
                      onClick={() => { setAnswers((a) => ({ ...a, budget: key })); setTimeout(goNext, 250); }}
                      className={cn(
                        "flex flex-col items-start gap-2 p-6 rounded-2xl border-2 transition-all duration-200 text-left",
                        "bg-white/5 hover:bg-white/10 hover:scale-[1.02] backdrop-blur-sm",
                        active ? "border-secondary bg-white/10" : "border-white/10"
                      )}
                    >
                      <Wallet className={cn("h-7 w-7 mb-2", active ? "text-secondary" : "text-white/80")} />
                      <span className="font-display text-xl font-semibold">{t(`steps.budget.options.${key}`)}</span>
                      <span className="text-xs text-white/60">{t(`steps.budget.hint.${key}`)}</span>
                    </button>
                  );
                })}
              </div>
            </StepWrap>
          )}

          {step === "results" && (
            <div className="animate-fade-in">
              <div className="text-center mb-8 pt-2">
                <Activity className="h-10 w-10 mx-auto text-secondary mb-3" />
                <h2 className="font-display text-2xl md:text-4xl font-semibold mb-3">{t("results.title")}</h2>
                <p className="text-white/80 max-w-2xl mx-auto">{t("results.intro", { trip: tripPhrase })}</p>
              </div>

              {loading && (
                <div className="text-center py-16 text-white/70">{t("results.loading")}</div>
              )}

              {!loading && results.length === 0 && (
                <div className="text-center py-16 text-white/70">{t("results.empty")}</div>
              )}

              {!loading && results.length > 0 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    {results.map((it) => (
                      <a
                        key={it.id}
                        href={it.href}
                        className="group block rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-secondary/60 hover:bg-white/10 transition-all hover:scale-[1.02]"
                      >
                        <div className="aspect-[4/3] bg-white/5 overflow-hidden">
                          {it.image ? (
                            <img src={it.image} alt={it.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/30">
                              <Waves className="h-10 w-10" />
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="text-[10px] uppercase tracking-[0.15em] text-secondary/90 mb-1">{it.reason}</div>
                          <div className="font-display text-base font-semibold line-clamp-2 mb-2">{it.title}</div>
                          <div className="flex items-center justify-between text-xs text-white/70">
                            <span>{it.priceLabel ?? ""}</span>
                            <span className="flex items-center gap-1 text-secondary group-hover:translate-x-0.5 transition-transform">
                              {t("results.viewDetails")} <ExternalLink className="h-3 w-3" />
                            </span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <Button
                      asChild
                      className="h-12 px-8 rounded-full font-body font-semibold text-[12px] tracking-[0.14em] uppercase bg-secondary text-secondary-foreground hover:bg-secondary/90"
                    >
                      <a href={buildWhatsappUrl()} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="h-4 w-4 mr-2" /> {t("results.whatsapp")}
                      </a>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={restart}
                      className="h-12 px-6 rounded-full font-body font-semibold text-[12px] tracking-[0.14em] uppercase bg-white/5 border-white/30 text-white hover:bg-white/10 hover:text-white"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" /> {t("results.restart")}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Next button for steps that need it */}
          {(step === "days" || step === "vibe") && (
            <div className="flex justify-center mt-8">
              <Button
                disabled={!canProceed()}
                onClick={goNext}
                className="h-12 px-10 rounded-full font-body font-semibold text-[12px] tracking-[0.14em] uppercase bg-secondary text-secondary-foreground hover:bg-secondary/90 disabled:opacity-40"
              >
                {stepIdx === FLOW.length - 2 ? t("seeResults") : t("next")}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StepWrap = ({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) => (
  <div className="animate-fade-in">
    <div className="text-center mb-6 md:mb-8 mt-4">
      <h2 className="font-display text-2xl md:text-4xl font-semibold mb-2 leading-tight">{title}</h2>
      <p className="text-white/70 text-sm md:text-base">{subtitle}</p>
    </div>
    {children}
  </div>
);

export default TripQuizDialog;
